/**
 * @module pages/PrintStudioPage
 * @description Print studio (#/print): every postcard, at a true postcard
 * size, two ways — on any printer from this device, or as a print-shop PDF
 * (FedEx Office, Vistaprint, a local shop) with bleed at 300 dpi.
 *
 * One page list feeds both outputs, so the browser print and the PDF can
 * never disagree about order or content: front, back, front, back…
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { StoryPostcard } from "../components/StoryPostcard";
import { StoryCardBack } from "../components/CardBacks";
import { ThankYouBack, ThankYouFront } from "../components/ThankYouPostcard";
import { settingsRepository, useCards, useSettings, useThankYouCards } from "../data";
import { absoluteUrlFor, hrefFor } from "../app/router";
import { useEdition } from "../app/edition";
import { BLEED_IN, PRINT_DPI, PRINT_SIZES, type PrintSizeId } from "../config/print";
import { usePrint, type PrintPage } from "../lib/print/PrintService";
import { FixedLandscape } from "../app/cardOrientation";
import { buildPrintShopPdf } from "../lib/print/printShopPdf";
import { downloadBlob } from "../lib/outputEngine";
import type { OrderedRecord, StoryCard, ThankYouCard } from "../domain/types";

type CardSet = "stories" | "thank-you";

/** Held records print only as clearly marked proofs. */
const Proofable = ({ held, children }: { held: boolean; children: ReactNode }) =>
    held ? (
        <div className="proof-wrap">
            {children}
            {/* A real element, not ::after: html-to-image does not reliably capture pseudo-elements. */}
            <span className="proof-mark" aria-hidden="true">
                Proof · content hold
            </span>
        </div>
    ) : (
        <>{children}</>
    );

/** Hosts that are previews, not the production site. QR codes printed from them point at the preview. */
const isPreviewHost = (url: string) => /localhost|127\.0\.0\.1|\.pages\.dev/.test(url);

export function PrintStudioPage() {
    const edition = useEdition();
    const settings = useSettings();
    const stories = useCards();
    const thankYous = useThankYouCards();
    const print = usePrint();

    const [set, setSet] = useState<CardSet>("stories");
    const [withBacks, setWithBacks] = useState(true);
    const [includeHeld, setIncludeHeld] = useState(false);
    const [deselected, setDeselected] = useState<Set<string>>(new Set());
    const [building, setBuilding] = useState(false);
    const [status, setStatus] = useState("");
    const stageRef = useRef<HTMLDivElement>(null);

    const size = PRINT_SIZES[settings.printSize];
    const records: OrderedRecord[] = set === "stories" ? stories : thankYous;
    const eligible = records.filter((r) => r.published || r.contentHold).filter((r) => includeHeld || !r.contentHold);
    const chosen = eligible.filter((r) => !deselected.has(r.id));

    /** The single ordered page list used by both outputs. */
    const pages: PrintPage[] = useMemo(
        () =>
            chosen.flatMap((record) => {
                const held = !!record.contentHold;
                const faces: PrintPage[] = [];
                if (set === "stories") {
                    const card = record as StoryCard;
                    faces.push({
                        key: `${card.id}-front`,
                        node: (
                            <Proofable held={held}>
                                <StoryPostcard card={card} edition={edition} />
                            </Proofable>
                        ),
                    });
                    if (withBacks)
                        faces.push({
                            key: `${card.id}-back`,
                            node: (
                                <Proofable held={held}>
                                    <StoryCardBack card={card} edition={edition} />
                                </Proofable>
                            ),
                        });
                } else {
                    const card = record as ThankYouCard;
                    faces.push({
                        key: `${card.id}-front`,
                        node: (
                            <Proofable held={held}>
                                <ThankYouFront card={card} />
                            </Proofable>
                        ),
                    });
                    if (withBacks)
                        faces.push({
                            key: `${card.id}-back`,
                            node: (
                                <Proofable held={held}>
                                    <ThankYouBack card={card} />
                                </Proofable>
                            ),
                        });
                }
                return faces;
            }),
        [chosen, set, withBacks, edition],
    );

    // PDF build: runs after the off-screen stage has rendered every page.
    useEffect(() => {
        if (!building || !stageRef.current) return;
        const faces = Array.from(stageRef.current.querySelectorAll<HTMLElement>(":scope > .stage-page > *"));
        let cancelled = false;
        (async () => {
            const blob = await buildPrintShopPdf(faces, {
                size,
                bleed: true,
                onProgress: (done, total) => !cancelled && setStatus(`Rendering page ${done} of ${total}…`),
            });
            if (cancelled) return;
            const date = new Date().toISOString().slice(0, 10);
            const label = set === "stories" ? `${edition.id}-postcards` : "thank-you-postcards";
            downloadBlob(blob, `AFJ_${label}_${size.id}_bleed_${date}.pdf`);
            setStatus(`Downloaded ${faces.length} pages.`);
        })()
            .catch((error: unknown) => {
                console.error("[PrintStudio] PDF build failed", error);
                setStatus(`PDF failed: ${error instanceof Error ? error.message : String(error)}`);
            })
            .finally(() => !cancelled && setBuilding(false));
        return () => {
            cancelled = true;
        };
    }, [building, size, set, edition.id]);

    const toggle = (id: string) =>
        setDeselected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const qrTarget = absoluteUrlFor({ name: "home" });
    const fileW = (size.widthIn + BLEED_IN * 2).toFixed(2);
    const fileH = (size.heightIn + BLEED_IN * 2).toFixed(2);
    const backName =
        set === "thank-you"
            ? "thank-you message and address side"
            : edition.storyCardBack === "pledge"
              ? "pledge card"
              : "mailable back with QR code";

    return (
        // The studio shows and captures cards exactly as printed: landscape on every device.
        <FixedLandscape>
            <section className="print-studio">
                <header className="admin-head">
                    <div>
                        <p className="pc-kicker">Print studio · {edition.label} edition</p>
                        <h1 className="section-title">Print the postcards</h1>
                    </div>
                    <a className="btn" href={hrefFor({ name: "admin" })}>
                        ← Admin
                    </a>
                </header>

                <div className="ps-grid">
                    <form className="admin-form ps-options" onSubmit={(e) => e.preventDefault()}>
                        <fieldset className="pf-section">
                            <legend className="pf-legend">What to print</legend>
                            <label className="pf-check">
                                <input type="radio" name="ps-set" checked={set === "stories"} onChange={() => setSet("stories")} />
                                <span>Story postcards ({edition.label} edition)</span>
                            </label>
                            <label className="pf-check">
                                <input type="radio" name="ps-set" checked={set === "thank-you"} onChange={() => setSet("thank-you")} />
                                <span>Thank-you postcards</span>
                            </label>
                            <label className="pf-check">
                                <input type="checkbox" checked={withBacks} onChange={(e) => setWithBacks(e.target.checked)} />
                                <span>Include backs ({backName})</span>
                            </label>
                            <label className="pf-check">
                                <input type="checkbox" checked={includeHeld} onChange={(e) => setIncludeHeld(e.target.checked)} />
                                <span>Include content-hold cards, marked PROOF</span>
                            </label>
                        </fieldset>

                        <fieldset className="pf-section">
                            <legend className="pf-legend">Size</legend>
                            {Object.values(PRINT_SIZES).map((s) => (
                                <label key={s.id} className="pf-check">
                                    <input
                                        type="radio"
                                        name="ps-size"
                                        checked={settings.printSize === s.id}
                                        onChange={() => settingsRepository.update({ printSize: s.id as PrintSizeId })}
                                    />
                                    <span>
                                        <strong>{s.label}</strong>
                                        <br />
                                        <small>{s.mailing}</small>
                                    </span>
                                </label>
                            ))}
                            {set === "stories" && withBacks && edition.storyCardBack === "pledge" && settings.printSize === "4x6" && (
                                <p className="pf-fine">Tip: the pledge card is written on by hand; 6 × 9 in gives guests room to write.</p>
                            )}
                        </fieldset>

                        <div className="ps-actions">
                            <button
                                type="button"
                                className="btn btn--primary btn--lg"
                                disabled={!pages.length || building}
                                onClick={() => print({ size, pages })}
                            >
                                Print on this device
                            </button>
                            <button
                                type="button"
                                className="btn btn--lg"
                                disabled={!pages.length || building}
                                onClick={() => {
                                    setStatus("Preparing…");
                                    setBuilding(true);
                                }}
                            >
                                {building ? "Building PDF…" : "Download print-shop PDF"}
                            </button>
                            <p className="output-status" aria-live="polite">
                                {status}
                            </p>
                        </div>
                    </form>

                    <aside className="ps-spec">
                        <h2>For the print shop</h2>
                        <dl>
                            <dt>File</dt>
                            <dd>
                                PDF, {pages.length} pages, each {fileW} × {fileH} in: {size.widthIn} × {size.heightIn} in plus {BLEED_IN} in
                                bleed, {PRINT_DPI} dpi.
                            </dd>
                            <dt>Order as</dt>
                            <dd>{size.orderAs}.</dd>
                            <dt>Page order</dt>
                            <dd>
                                {withBacks
                                    ? 'Front, back, front, back… Both sides are landscape and upright ("head-to-head").'
                                    : "Fronts only, single-sided."}
                            </dd>
                            <dt>Trim</dt>
                            <dd>
                                Trim the {BLEED_IN} in bleed from every edge. No crop marks are added; shops' upload portals expect none.
                            </dd>
                            <dt>Mailing</dt>
                            <dd>{size.mailing}</dd>
                        </dl>
                        <h2>On this device</h2>
                        <p>
                            In the print dialog choose paper size {size.widthIn} × {size.heightIn} in, scale 100% (Actual size), margins
                            None. For two-sided printing use the printer's two-sided setting; if backs come out upside down, switch between
                            "flip on long edge" and "flip on short edge".
                        </p>
                        {set === "stories" && edition.storyCardBack === "mail" && withBacks && (
                            <p className={isPreviewHost(qrTarget) ? "pf-error" : ""}>
                                QR codes on the backs point to <strong>{qrTarget}</strong>
                                {isPreviewHost(qrTarget) &&
                                    " — this is a preview address. Set the production address before printing a final run."}
                            </p>
                        )}
                    </aside>
                </div>

                <h2 className="ps-count">
                    {chosen.length} of {eligible.length} cards selected · {pages.length} pages
                </h2>
                <ul className="ps-thumbs">
                    {eligible.map((record) => {
                        const on = !deselected.has(record.id);
                        return (
                            <li key={record.id} className={on ? "" : "is-off"}>
                                <label>
                                    <input type="checkbox" checked={on} onChange={() => toggle(record.id)} />
                                    <span>
                                        {set === "stories" ? (record as StoryCard).title : (record as ThankYouCard).name}
                                        {record.contentHold ? " · PROOF" : ""}
                                    </span>
                                </label>
                                {set === "stories" ? (
                                    <StoryPostcard card={record as StoryCard} edition={edition} />
                                ) : (
                                    <ThankYouFront card={record as ThankYouCard} />
                                )}
                            </li>
                        );
                    })}
                </ul>

                {/* Off-screen stage: laid out (so it can be captured) but not visible. Mounted only while building. */}
                {building && (
                    <div className="print-stage" ref={stageRef} aria-hidden="true">
                        {pages.map((page) => (
                            <div key={page.key} className="stage-page">
                                {page.node}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </FixedLandscape>
    );
}
